import React from "react";

/**
 * "Is it really SAINT®? 5 checks" box.
 *
 * Shown on every /learn article (EN). The 5 checks supersede the earlier
 * 4-check version from Prompt 8. No competitor is named.
 */
const CHECKS_EN = [
  {
    q: "Is the clinic listed on Magnus Medical's provider locator?",
    a: "If a clinic isn't on the official Magnus Medical locator (magnusmedical.com), it is not a verified SAINT® provider.",
    href: "https://www.magnusmedical.com/find-a-provider",
  },
  {
    q: "Do they do a brain MRI to personalize your target?",
    a: "SAINT® requires a brain MRI to find your individual target. No scan = not SAINT®.",
    href: undefined,
  },
  {
    q: "Which TMS machine and software do they use?",
    a: "SAINT® runs only on the Magnus Medical system with its FDA-cleared targeting software.",
    href: undefined,
  },
  {
    q: "Is the 5-day protocol FDA-cleared, or off-label?",
    a: "Ask the clinic to state in writing whether their 5-day protocol is FDA-cleared or an off-label schedule.",
    href: undefined,
  },
  {
    q: "Do they offer it to minors?",
    a: "SAINT® is FDA-cleared for adults only. Be cautious if a clinic offers an accelerated course to a teen or child.",
    href: undefined,
  },
];

const SaintFiveChecksBox: React.FC<{ es?: boolean }> = ({ es = false }) => {
  const checks = es
    ? [
        {
          q: "¿Aparece la clínica en el localizador de proveedores de Magnus Medical?",
          a: "Si una clínica no está en el localizador oficial de Magnus Medical (magnusmedical.com), no es un proveedor verificado de SAINT®.",
          href: "https://www.magnusmedical.com/find-a-provider",
        },
        {
          q: "¿Le hacen una resonancia magnética cerebral para personalizar su objetivo?",
          a: "SAINT® requiere una resonancia cerebral para encontrar su objetivo individual. Sin resonancia = no es SAINT®.",
          href: undefined,
        },
        {
          q: "¿Qué máquina y software de TMS usan?",
          a: "SAINT® funciona solo en el sistema Magnus Medical con su software de orientación autorizado por la FDA.",
          href: undefined,
        },
        {
          q: "¿El protocolo de 5 días está autorizado por la FDA o es off-label?",
          a: "Pida a la clínica que conste por escrito si su protocolo de 5 días está autorizado por la FDA o es un calendario off-label.",
          href: undefined,
        },
        {
          q: "¿Lo ofrecen a menores?",
          a: "SAINT® está autorizado por la FDA solo para adultos. Tenga cuidado si una clínica ofrece un curso acelerado a un adolescente o niño.",
          href: undefined,
        },
      ]
    : CHECKS_EN;

  return (
    <aside
      className="not-prose mt-10 rounded-2xl border border-accent/30 bg-accent/5 p-5 sm:p-6"
      aria-label={
        es
          ? "5 comprobaciones para verificar SAINT"
          : "5 checks to verify SAINT"
      }
    >
      <h2 className="font-headings text-lg font-bold text-primary sm:text-xl">
        {es
          ? "¿Es realmente SAINT®? 5 comprobaciones"
          : "Is it really SAINT®? 5 checks"}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {es
          ? "Antes de pagar por cualquier programa de 5 días anunciado como SAINT®, confirme estos cinco puntos."
          : "Before you pay for any 5-day program advertised as SAINT®, confirm these five points."}
      </p>
      <ol className="mt-4 space-y-3">
        {checks.map((c, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent">
              {i + 1}
            </span>
            <div>
              <p className="font-semibold text-primary">{c.q}</p>
              <p className="text-sm leading-relaxed text-foreground/85">
                {c.a}
                {c.href && (
                  <>
                    {" "}
                    <a
                      href={c.href}
                      target="_blank"
                      rel="noopener"
                      className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
                    >
                      {es
                        ? "Localizador de Magnus Medical"
                        : "Magnus Medical locator"}
                    </a>
                  </>
                )}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
};

export default SaintFiveChecksBox;
